import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Check, CalendarIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Stepper from "@/components/Stepper";
import { useTrip } from "@/context/TripContext";

export default function PackageDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { setTrip } = useTrip();
  const [p, setP] = useState(null);
  const [checkIn, setCheckIn] = useState(addDays(new Date(), 1));
  const [avail, setAvail] = useState(null);
  const [slotId, setSlotId] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  useEffect(() => { api.get(`/packages/${slug}`).then((r) => setP(r.data)).catch((e) => toast.error(formatErr(e))); }, [slug]);

  useEffect(() => {
    if (!p || !checkIn) return;
    api.get(`/packages/${p.id}/availability`, { params: { check_in: format(checkIn, "yyyy-MM-dd") } })
      .then((r) => { setAvail(r.data); setSlotId(r.data.slots.find((s) => s.remaining_vehicles > 0)?.id || ""); })
      .catch((e) => toast.error(formatErr(e)));
    setTrip({ safari: p.name, date: format(checkIn, "dd MMM yyyy") });
  }, [p, checkIn, setTrip]);

  if (!p) return <div className="jt-container py-20">Loading…</div>;
  const maxGuests = p.room.max_guests;
  const roomsNeeded = Math.ceil((adults + children) / maxGuests);
  const jeepsNeeded = Math.ceil((adults + children) / 6);
  const canBook = avail && avail.rooms_left >= roomsNeeded && slotId;

  const book = () => {
    const q = new URLSearchParams({ kind: "package", package_id: p.id, check_in: format(checkIn, "yyyy-MM-dd"),
      slot_id: slotId, adults: String(adults), children: String(children) });
    nav(`/checkout?${q}`);
  };

  return (
    <div>
      <div className="relative h-[50vh] overflow-hidden">
        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 jt-container pb-10 text-white">
          <div className="jt-eyebrow text-[#D4A359] mb-3">{p.nights} nights · stay + safari</div>
          <h1 className="font-display text-4xl md:text-5xl">{p.name}</h1>
          <p className="mt-3 text-[#E5E0D6] max-w-2xl">{p.short_description}</p>
        </div>
      </div>

      <div className="jt-container py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <p className="leading-relaxed">{p.description}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Part eyebrow="Your stay" title={p.hotel.name} sub={`${p.room.name} · up to ${maxGuests} guests`} img={p.hotel.image_url}/>
            <Part eyebrow="Your safari" title={p.safari.name} sub={`Private jeep · ${p.safari.duration_hours} hours`} img={p.safari.image_url}/>
          </div>
          <div>
            <h3 className="font-serif text-xl mb-3 text-[#1E3A2B]">Included</h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {p.inclusions.map((x) => <li key={x} className="flex gap-2 text-sm"><Check size={16} className="text-[#1E3A2B] mt-0.5"/>{x}</li>)}
            </ul>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start bg-white rounded-2xl ring-1 ring-black/5 p-6 shadow-xl" data-testid="package-book-box">
          <div className="font-serif text-3xl text-[#1E3A2B]">{inr(p.price)}</div>
          <div className="text-xs text-[#5C6B63] mb-5">per package (1 room) · {p.nights} nights + 1 private safari</div>

          <label className="jt-eyebrow mb-2 block">Check-in</label>
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-11 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm mb-1" data-testid="package-checkin-trigger">
                <CalendarIcon size={15} className="text-[#5C6B63]"/>{format(checkIn, "dd MMM yyyy")}
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
              <Calendar mode="single" selected={checkIn} onSelect={(d) => d && setCheckIn(d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}/>
            </PopoverContent>
          </Popover>
          {avail && <p className="text-xs text-[#5C6B63] mb-4">Check-out {avail.check_out} · <span data-testid="package-rooms-left">{avail.rooms_left} rooms left</span></p>}

          <label className="jt-eyebrow mb-2 block">Safari during your stay</label>
          <div className="flex flex-col gap-2 mb-4 max-h-52 overflow-y-auto">
            {avail?.slots.length === 0 && <span className="text-sm text-[#5C6B63]">No safari slots open for these dates yet.</span>}
            {avail?.slots.map((s) => {
              const ok = s.remaining_vehicles > 0;
              return (
                <button key={s.id} disabled={!ok} onClick={() => setSlotId(s.id)} data-testid={`package-slot-${s.id}`}
                  className={`px-3 py-2 rounded-md text-sm text-left border flex items-center gap-2 ${slotId === s.id ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF]"} ${!ok ? "opacity-40" : ""}`}>
                  <Clock size={13}/>{format(new Date(s.date + "T00:00"), "EEE dd MMM")} · {s.departure_time} {s.label}{s.started ? " · safari started" : !ok && " · full"}
                </button>
              );
            })}
          </div>
          <Stepper label="Adults" value={adults} setValue={setAdults} min={1} max={30} testid="package-adults"/>
          <Stepper label="Children" value={children} setValue={setChildren} max={20} testid="package-children"/>
          <p className="text-xs text-[#5C6B63] mt-1" data-testid="package-auto-note">{roomsNeeded} room{roomsNeeded > 1 ? "s" : ""} · {jeepsNeeded} jeep{jeepsNeeded > 1 ? "s" : ""} (up to {maxGuests} per room, 6 per jeep). Extra rooms and jeeps are added to the price at checkout.</p>
          <button onClick={book} disabled={!canBook} data-testid="package-book-btn"
            className={`jt-pill-accent w-full justify-center mt-5 ${!canBook ? "opacity-40 cursor-not-allowed" : ""}`}>
            Book package
          </button>
        </aside>
      </div>
    </div>
  );
}

function Part({ eyebrow, title, sub, img }) {
  return (
    <div className="bg-[#F5F0EB] rounded-2xl overflow-hidden">
      <img src={img} alt="" className="h-36 w-full object-cover"/>
      <div className="p-4">
        <div className="jt-eyebrow mb-1">{eyebrow}</div>
        <div className="font-serif text-lg">{title}</div>
        <div className="text-xs text-[#5C6B63]">{sub}</div>
      </div>
    </div>
  );
}
